// Corporate Health page.
//
// Static port of the design system in corporate.html. All copy is hard
// coded — this is brand/marketing content that doesn't need admin
// curation. The lead form opens a prefilled mailto (no backend) so a
// captured enquiry always lands in the corporate@ inbox.
//
// Layout (12-col grid, wrapped in MainLayout — existing nav+footer):
//   1. Dark hero with eyebrow / split title / lede / CTAs + live HR
//      dashboard preview card on the right
//   2. Stats bar (4 KPIs)
//   3. Why Partner pillars (6 cards)
//   4. Programmes (8 cards on a paper-2 background)
//   5. Sample AHC tiers (4 cards, middle one featured "Most chosen")
//   6. How We Work (5 process steps on a dark band)
//   7. HR Dashboard story (5 features + mock dashboard visual)
//   8. Industries (8 tiles)
//   9. FAQ accordion (8 items)
//   10. Lead form + side bullets

import { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import { useSubmitCorporateEnquiryMutation } from '../store/siteContentApi';

// ─── Design tokens (mirror corporate.html) ─────────────────────
const C = {
  teal:     '#00B49A',
  tealDark: '#00897A',
  gold:     '#F0B429',
  ink:      '#1A3A2A',
  inkSoft:  '#2C4A3A',
  paper:    '#FAF8F3',
  paper2:   '#F2EEE4',
  line:     '#D9D3C4',
  muted:    '#6B7570',
  danger:   '#B0421A',
};

const CRM_EMAIL = 'corporate@mytrustlab.co.in';

// ─── Static content ────────────────────────────────────────────
const PILLARS = [
  { num: '01', title: 'Programme design, not a price list',
    body: "We start with your workforce profile — age mix, roles, risk exposure — and design the package. You don't pick from a menu; we co-design the menu." },
  { num: '02', title: 'Logistics that scale',
    body: 'A growing branch network and a mobile camp infrastructure. We handle on-site phlebotomy, multi-city rollouts, shift coverage, and re-tests without losing track of a single sample.' },
  { num: '03', title: 'Insights your HR team can use',
    body: 'A live dashboard with anonymised aggregate trends, risk segmentation, and YoY comparison — so your wellness budget goes where it actually moves the needle.' },
  { num: '04', title: 'Compliance built in',
    body: 'Factories Act, Shop & Establishment, food handler, drivers\' fitness, ESI-aligned panels — documented, audit-ready, and signed off by qualified physicians.' },
  { num: '05', title: 'Predictable, transparent billing',
    body: 'Per-employee rates with no hidden add-ons. GST-compliant invoicing, single PO for multi-location engagements, and a named account manager.' },
  { num: '06', title: 'Privacy by design',
    body: 'Individual reports go to the employee. HR sees only de-identified aggregates — fully aligned to the Digital Personal Data Protection Act, 2023.' },
];

const PROGRAMS = [
  { tag: 'Pre-Onboarding', title: 'Pre-Employment Medicals',
    desc: 'Standardised medical screening for new hires, structured by role and statutory requirement.',
    bullets: ['Identity verification & record matching', 'Role-specific test panels', 'Fit / Conditional Fit / Unfit certification', 'Sub-24-hour TAT for bulk onboarding'] },
  { tag: 'Yearly', title: 'Annual Health Checkups',
    desc: 'The flagship preventive screening for the full workforce, built around clinical evidence.',
    bullets: ['Tiered packages (Essential to Premier)', 'Doctor consultation included', 'Spouse / family extension available', 'Branch visit, on-site camp, or hybrid'] },
  { tag: 'Senior Leadership', title: 'Executive Health Programme',
    desc: 'Comprehensive longevity-grade screening for the C-suite, leadership, and high-stress roles.',
    bullets: ['100+ biomarkers across 12 systems', 'Cardiac, cancer marker & metabolic panels', 'One-to-one physician consultation', 'Concierge appointment & report walkthrough'] },
  { tag: 'On-Site', title: 'On-Site Health Camps',
    desc: 'We bring the lab to your office — phlebotomists, vitals, ECG, reception, the lot.',
    bullets: ['Multi-day, multi-shift coverage', 'Full setup: registration, vitals, sample collection', 'Optional doctor / dietitian / physio booth', 'Branded patient experience for your team'] },
  { tag: 'Statutory', title: 'Statutory & Compliance Medicals',
    desc: 'Audit-ready medical examinations aligned with applicable Indian regulations.',
    bullets: ['Factories Act 1948 schedules', 'Shop & Establishment Act medicals', 'Food Handler & FSSAI panels', "Drivers' fitness & vision certification"] },
  { tag: 'High-Risk Roles', title: 'Specialised Industry Panels',
    desc: 'Targeted surveillance for occupational exposure and industry-specific risk.',
    bullets: ['Heavy metal & solvent exposure (manufacturing)', 'Audiometry & spirometry (industrial)', 'Eye-strain & ergonomic panels (IT)', 'Infectious disease screening (healthcare)'] },
  { tag: 'Wellness', title: 'Wellness & Awareness Drives',
    desc: 'Calendar-based engagement that keeps health visible all year, not just at AHC time.',
    bullets: ['World Health Day / Heart Day events', 'Doctor talks & expert AMAs', 'Walk-in screening pop-ups', "Women's health & menopause programmes"] },
  { tag: 'Custom', title: 'Build Your Own',
    desc: 'Got a specific brief — campus health, ESI top-up, returnship medicals, M&A due-diligence health audit? Tell us what you need, we design the programme around it.',
    bullets: ['Custom panel design by qualified pathologists', 'Costed in days, not weeks', 'Single SPOC throughout'] },
];

const PROCESS = [
  { num: '01', title: 'Discover', body: '30-minute call with your HR & Admin teams. We map workforce profile, locations, statutory needs, and budget envelope.' },
  { num: '02', title: 'Design',   body: 'Costed proposal with package recommendations, logistics plan, and an example dashboard — turned around in 3 working days.' },
  { num: '03', title: 'Roll Out', body: 'Multi-location, multi-shift coordinated execution. On-site phlebotomy or branch visits, run by a dedicated camp manager.' },
  { num: '04', title: 'Report',   body: 'Individual reports go to employees within 24 hours. Aggregate, anonymised dashboard goes live for HR.' },
  { num: '05', title: 'Refine',   body: "Quarterly review with your account manager. We compare YoY trends and recalibrate next year's programme accordingly." },
];

const DASH_FEATURES = [
  ['1', 'Risk segmentation by department',  'See where the high-risk clusters sit, so wellness budgets land where they matter.'],
  ['2', 'Year-over-year trend lines',       'Track whether your interventions actually moved metabolic and cardiac risk markers.'],
  ['3', 'Statutory compliance reporting',   'One-click reports for Factories Act audits, ESI submissions, and CSR disclosure.'],
  ['4', 'Programme participation rates',    'Granular tracking by location, function, shift — flagging where uptake is lagging.'],
  ['5', 'Exportable for board reviews',     'Slide-ready charts and CSV exports for ESG, board, and investor reporting.'],
];

const INDUSTRIES = [
  { code: 'IT',   name: 'IT & ITES',              meta: 'Sedentary · Eye-strain' },
  { code: 'BFSI', name: 'Banking & Financial',    meta: 'Stress · Cardiac risk' },
  { code: 'MFG',  name: 'Manufacturing',          meta: 'Exposure · Statutory' },
  { code: 'HC',   name: 'Healthcare & Pharma',    meta: 'Infection risk · Vaccines' },
  { code: 'RTL',  name: 'Retail & FMCG',          meta: 'Distributed · Shift work' },
  { code: 'LOG',  name: 'Logistics & Transport',  meta: "Drivers' fitness · Vision" },
  { code: 'EDU',  name: 'Education & Campus',     meta: 'Bulk · Cyclical' },
  { code: 'PSU',  name: 'Government & PSU',       meta: 'Empanelled · Audit-ready' },
];

const FAQS = [
  { q: "What's the minimum workforce size you take on?",
    a: "There's no hard floor. We've run programmes for teams of 30 and engagements of 5,000+. For smaller teams, branch-based AHCs are usually the most cost-effective option; on-site camps become economical from around 80–100 employees per location." },
  { q: "Do you cover locations where you don't have a branch?",
    a: 'Yes. Our owned branches anchor major metros and Tier-2 cities, and we extend coverage through validated collection partners and mobile camp infrastructure. Sample integrity is maintained end-to-end with cold-chain logistics and time-stamped chain-of-custody.' },
  { q: 'How do you handle individual privacy?',
    a: 'Individual reports go directly to the employee through a secure portal — never to HR. The HR dashboard only ever shows de-identified aggregates with statistical thresholds to prevent re-identification. The whole programme is aligned with the Digital Personal Data Protection Act, 2023 and our published Privacy Policy.' },
  { q: 'What does pricing look like?',
    a: 'Per-employee pricing, scaled by package and volume. We share a fully itemised quotation within 3 working days of the discovery call, with no hidden add-ons. Multi-year commitments and family-floater extensions get preferential rates.' },
  { q: 'How long does report turnaround take for a 500-person camp?',
    a: 'For standard panels, individual reports are released within 24 hours of sample receipt. For panels including imaging or specialty tests (ECHO, TMT, USG), the turnaround is 48–72 hours. Aggregated HR dashboards are live within 5 working days of camp closure.' },
  { q: 'Can you integrate with our HRIS / wellness platform?',
    a: 'Yes — we offer secure API integration to push test status, completion data, and de-identified aggregates into your existing HRIS, wellness platform, or insurance broker system. Common integrations are configured in 2–3 weeks.' },
  { q: 'Do you handle re-tests and inconclusive results?',
    a: 'Re-tests for technically inconclusive results are included in the engagement at no extra cost. Re-tests for clinically suspicious findings are flagged to the employee and their treating physician, with our pathologist available for clarification.' },
  { q: 'Can you support our CSR or rural outreach programmes?',
    a: 'Yes. We run subsidised CSR-aligned health camps for community programmes — schools, construction sites, NGO outreach, and rural drives. Speak to our team about eligibility and mobile-camp availability in your operating geographies.' },
];

const PROGRAM_OPTIONS = [
  ['PEM',       'Pre-Employment Medicals'],
  ['AHC',       'Annual Health Checkup'],
  ['EHP',       'Executive Programme'],
  ['Camp',      'On-Site Camp'],
  ['Statutory', 'Statutory Medicals'],
  ['Wellness',  'Wellness & Awareness'],
  ['Custom',    'Custom / Not Sure Yet'],
];

const INDUSTRY_OPTIONS = [
  'IT & ITES', 'Banking & Financial Services', 'Manufacturing',
  'Healthcare & Pharma', 'Retail & FMCG', 'Logistics & Transport',
  'Education', 'Government / PSU', 'Other',
];

const SIZE_OPTIONS = ['Up to 100', '100–500', '500–1,000', '1,000–5,000', '5,000+'];

const TIMELINE_OPTIONS = [
  'Immediate (within 30 days)', 'This quarter', 'Next quarter',
  'Annual planning cycle', 'Just exploring',
];

// ─── Page ──────────────────────────────────────────────────────
const Corporate = () => (
  <Box sx={{ bgcolor: C.paper, color: C.ink }}>
    <Hero />
    <StatsBar />
    <WhyPartner />
    <Programs />
    {/* Existing 4-tier "Sample tiers / Indicative tiers" panel is hidden
        — its heading is now reused inside PackageArchitecture for the
        three-family cards. Flip to `<PackageTiers />` to restore. */}
    {/* <PackageTiers /> */}
    <PackageArchitecture />
    <HowWeWork />
    <DashboardStory />
    <Industries />
    <FaqSection />
    <LeadFormSection />
  </Box>
);

// ─── Hero ─────────────────────────────────────────────────────
const Hero = () => (
  <Box sx={{
    bgcolor: C.ink, color: C.paper,
    pt: { xs: 8, md: 13 }, pb: { xs: 12, md: 15 },
    px: { xs: 2.5, md: 4 },
    position: 'relative', overflow: 'hidden',
    '&::before': {
      content: '""', position: 'absolute', inset: 0,
      background:
        'radial-gradient(circle at 85% 20%, rgba(0,180,154,0.22), transparent 50%),' +
        'radial-gradient(circle at 5% 90%, rgba(240,180,41,0.13), transparent 55%)',
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""', position: 'absolute',
      bottom: -1, left: 0, right: 0, height: 60,
      background: `linear-gradient(to bottom, transparent, ${C.paper})`,
      pointerEvents: 'none',
    },
  }}>
    <Container maxWidth="lg" sx={{ position: 'relative' }}>
      <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
        <Grid item xs={12} md={6}>
          <Eyebrow>Corporate Health</Eyebrow>
          <Box component="h1" sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: { xs: 44, sm: 60, md: 80 },
            lineHeight: 0.95, letterSpacing: '1px',
            m: 0, mb: 2.5, color: C.paper,
          }}>
            Workforce health,<br />
            <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>at scale</Box>.
          </Box>
          <Box sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15.5, lineHeight: 1.6,
            color: 'rgba(250,248,243,0.82)',
            maxWidth: 520, mb: 4,
          }}>
            Pre-employment medicals, annual checkups, executive programmes, and on-site camps —
            delivered across our NABL-accredited network, with a real-time HR dashboard
            that turns thousands of test results into decisions you can act on.
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <CtaLink href="#enquiry" variant="primary">Request a quote →</CtaLink>
            <CtaLink href="#programs" variant="ghost">Explore programmes</CtaLink>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <DashboardPreview />
        </Grid>
      </Grid>
    </Container>
  </Box>
);

// ─── Hero dashboard preview ────────────────────────────────────
const DashboardPreview = () => (
  <Box sx={{
    bgcolor: 'rgba(250,248,243,0.04)',
    border: '1px solid rgba(250,248,243,0.12)',
    borderRadius: '12px',
    p: 3.5,
    backdropFilter: 'blur(8px)',
    position: 'relative',
  }}>
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      mb: 2.5, pb: 2, borderBottom: '1px solid rgba(250,248,243,0.1)',
    }}>
      <Box sx={{
        fontFamily: "'Space Mono', monospace",
        fontSize: 11, letterSpacing: '2px', color: C.gold,
        textTransform: 'uppercase',
      }}>
        Workforce Health · Q1 Snapshot
      </Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        fontFamily: "'Space Mono', monospace",
        fontSize: 10, color: C.teal,
        letterSpacing: '1.5px', textTransform: 'uppercase',
        '&::before': {
          content: '""', width: 8, height: 8,
          bgcolor: C.teal, borderRadius: '50%',
          animation: 'corp-pulse 2s infinite',
        },
      }}>
        Live
      </Box>
    </Box>

    <Grid container spacing={2} sx={{ mb: 3 }}>
      {[
        ['2,847', 'Screened YTD', true],
        ['94%',   'Participation', false],
        ['< 24h', 'Report TAT',    true],
        ['11',    'Cities Active', false],
      ].map(([num, lbl, teal]) => (
        <Grid item xs={6} key={lbl}>
          <Box sx={{ bgcolor: 'rgba(250,248,243,0.04)', p: 2, borderRadius: 1 }}>
            <Box sx={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 32, lineHeight: 1, letterSpacing: '1px',
              color: teal ? C.teal : C.paper,
            }}>
              {num}
            </Box>
            <Box sx={{
              fontFamily: "'Space Mono', monospace",
              fontSize: 10, letterSpacing: '1.5px',
              color: 'rgba(250,248,243,0.6)', textTransform: 'uppercase',
              mt: 0.75,
            }}>
              {lbl}
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>

    {/* Risk bars */}
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      {[
        ['Healthy',     C.teal,   '64%', 64],
        ['Borderline',  C.gold,   '24%', 24],
        ['High Risk',   C.danger, '12%', 12],
      ].map(([label, color, pct, w]) => (
        <Box key={label} sx={{
          display: 'grid', gridTemplateColumns: '80px 1fr 40px',
          gap: 1.5, alignItems: 'center',
          fontFamily: "'Space Mono', monospace",
          fontSize: 11, letterSpacing: '1px',
          color: 'rgba(250,248,243,0.7)',
        }}>
          <span>{label}</span>
          <Box sx={{
            height: 8, bgcolor: 'rgba(250,248,243,0.08)',
            borderRadius: 0.5, overflow: 'hidden',
          }}>
            <Box sx={{
              height: '100%', width: `${w}%`,
              bgcolor: color, borderRadius: 0.5,
              animation: 'corp-grow 1.4s ease-out',
            }} />
          </Box>
          <Box sx={{ textAlign: 'right', color: C.paper }}>{pct}</Box>
        </Box>
      ))}
    </Box>

    {/* Keyframes — co-located with the component that uses them. */}
    <Box component="style" dangerouslySetInnerHTML={{ __html: `
      @keyframes corp-pulse {
        0%,100% { opacity: 1; box-shadow: 0 0 0 0 rgba(0,180,154,0.7); }
        50%     { opacity: 0.6; box-shadow: 0 0 0 8px rgba(0,180,154,0); }
      }
      @keyframes corp-grow { from { width: 0; } }
    `}} />
  </Box>
);

// ─── Stats bar ─────────────────────────────────────────────────
const StatsBar = () => {
  const items = [
    { value: '14',   label: 'Branches Pan-India',     accent: 'value' },
    { value: 'NABL', label: 'ISO 15189 Accredited',   accent: null },
    { value: '29',   label: 'Configurable Packages',  accent: 'value' },
    { value: '24',   label: 'Standard Report TAT',    accent: 'tat' },
  ];
  return (
    <Box sx={{
      bgcolor: C.paper, py: 6, px: { xs: 2.5, md: 4 },
      borderBottom: `1px solid ${C.line}`,
      mt: '-30px', position: 'relative', zIndex: 2,
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {items.map((s) => (
            <Grid item xs={6} md={3} key={s.label} sx={{ textAlign: 'center' }}>
              <Box sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: { xs: 36, md: 48 },
                lineHeight: 1, letterSpacing: '1px', color: C.ink,
              }}>
                {s.accent === 'value' ? <Box component="span" sx={{ color: C.teal }}>{s.value}</Box> :
                 s.accent === 'tat'   ? <>&lt;<Box component="span" sx={{ color: C.teal }}>{s.value}</Box>h</> :
                 s.value}
              </Box>
              <Box sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 11, letterSpacing: '2px',
                color: C.muted, textTransform: 'uppercase', mt: 1,
              }}>
                {s.label}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

// ─── Why Partner pillars ───────────────────────────────────────
const WhyPartner = () => (
  <Container id="why" maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, px: { xs: 2.5, md: 4 } }}>
    <SectionHeading
      eyebrow="Why partner with TrustLab"
      title={<>More than a vendor.<br />A <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>workforce health partner</Box>.</>}
      lede="Most labs run your tests and email you reports. We build your programme around you — and stay involved long after the camp closes."
    />
    <Grid container spacing={0} sx={{ border: `1px solid ${C.line}`, borderRadius: '6px', overflow: 'hidden', bgcolor: C.line }}>
      {PILLARS.map((p) => (
        <Grid item xs={12} sm={6} md={4} key={p.num} sx={{
          bgcolor: C.paper, p: { xs: 4, md: 4.5 },
          transition: 'background 0.2s',
          '&:hover': { bgcolor: C.paper2 },
        }}>
          <Box sx={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: '2px', color: C.gold, mb: 2.5 }}>
            {p.num}
          </Box>
          <Box component="h3" sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 22, letterSpacing: '1px', color: C.ink,
            m: 0, mb: 1.25, lineHeight: 1.1,
          }}>
            {p.title}
          </Box>
          <Box sx={{ fontSize: 14, color: C.inkSoft, m: 0, lineHeight: 1.55 }}>
            {p.body}
          </Box>
        </Grid>
      ))}
    </Grid>
  </Container>
);

// ─── Programmes ────────────────────────────────────────────────
const Programs = () => (
  <Box id="programs" sx={{ bgcolor: C.paper2, py: { xs: 8, md: 12 }, px: { xs: 2.5, md: 4 } }}>
    <Container maxWidth="lg">
      <SectionHeading
        eyebrow="Programmes"
        title={<>Seven ways we deliver <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>workforce health</Box>.</>}
        lede="From the first day a candidate joins, to executive longevity programmes, to wellness drives — we cover the full lifecycle. Every programme can be combined or customised."
      />
      <Grid container spacing={2.5}>
        {PROGRAMS.map((p) => (
          <Grid item xs={12} sm={6} md={4} key={p.title}>
            <Box sx={{
              bgcolor: C.paper, border: `1px solid ${C.line}`, borderRadius: '6px',
              p: { xs: 3, md: 3.5 },
              transition: 'all 0.2s',
              position: 'relative', overflow: 'hidden',
              height: '100%',
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0,
                width: 4, height: '100%', bgcolor: C.teal,
                transform: 'scaleY(0)', transformOrigin: 'top',
                transition: 'transform 0.25s',
              },
              '&:hover': { borderColor: C.teal },
              '&:hover::before': { transform: 'scaleY(1)' },
            }}>
              <Box sx={{
                display: 'inline-block',
                fontFamily: "'Space Mono', monospace",
                fontSize: 10, letterSpacing: '2px', color: C.gold,
                textTransform: 'uppercase',
                bgcolor: 'rgba(240,180,41,0.1)',
                px: 1.25, py: 0.5, borderRadius: 12,
                mb: 2,
              }}>
                {p.tag}
              </Box>
              <Box component="h3" sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 22, letterSpacing: '1px', color: C.ink,
                m: 0, mb: 1.25, lineHeight: 1.1,
              }}>
                {p.title}
              </Box>
              <Box sx={{ fontSize: 14, color: C.inkSoft, m: 0, mb: 2, lineHeight: 1.55 }}>
                {p.desc}
              </Box>
              <Box component="ul" sx={{ m: 0, pl: 2.25, fontSize: 13, color: C.inkSoft }}>
                {p.bullets.map((b) => (
                  <Box component="li" key={b} sx={{ mb: 0.5 }}>{b}</Box>
                ))}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

// ─── How We Work ───────────────────────────────────────────────
const HowWeWork = () => (
  <Box sx={{ bgcolor: C.ink, color: C.paper, py: { xs: 8, md: 12 }, px: { xs: 2.5, md: 4 } }}>
    <Container maxWidth="lg">
      <SectionHeading
        eyebrow="How we work" eyebrowColor={C.gold}
        title={<>From brief to <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>insights</Box>, in 5 steps.</>}
        lede="A predictable engagement model. Every step has a named owner, a timeline, and a deliverable — so you always know where the programme stands."
        inverse
      />
      <Grid container spacing={0} sx={{
        bgcolor: 'rgba(250,248,243,0.1)',
        borderRadius: '6px', overflow: 'hidden',
      }}>
        {PROCESS.map((s) => (
          <Grid item xs={12} sm={6} md={2.4} key={s.num} sx={{ bgcolor: C.ink, p: { xs: 3.5, md: 3 } }}>
            <Box sx={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: { xs: 44, md: 48 }, lineHeight: 1,
              color: C.teal, letterSpacing: '1px', mb: 2.5,
            }}>
              {s.num}
            </Box>
            <Box component="h3" sx={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 18, letterSpacing: '1px', color: C.paper,
              m: 0, mb: 1, lineHeight: 1.1,
            }}>
              {s.title}
            </Box>
            <Box sx={{ fontSize: 13, color: 'rgba(250,248,243,0.7)', m: 0, lineHeight: 1.55 }}>
              {s.body}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

// ─── HR Dashboard story ────────────────────────────────────────
const DashboardStory = () => (
  <Box sx={{ bgcolor: C.paper, py: { xs: 8, md: 12 }, px: { xs: 2.5, md: 4 } }}>
    <Container maxWidth="lg">
      <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
        <Grid item xs={12} md={6}>
          <Eyebrow color={C.teal}>HR Insights Dashboard</Eyebrow>
          <Box component="h2" sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: { xs: 30, sm: 40, md: 56 },
            lineHeight: 1.0, letterSpacing: '1px',
            color: C.ink, m: 0, mb: 2.5,
          }}>
            Test results are data.<br />
            <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>Insights</Box> are actionable.
          </Box>
          <Box sx={{ fontSize: 15.5, color: C.inkSoft, m: 0 }}>
            Every TrustLab corporate engagement comes with a real-time, role-based dashboard for your HR
            and admin teams. Only de-identified aggregates — never individual data — so you get
            visibility without violating privacy.
          </Box>

          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 4 }}>
            {DASH_FEATURES.map(([num, title, body]) => (
              <Box component="li" key={num} sx={{
                py: 2,
                borderBottom: `1px solid ${C.line}`,
                display: 'grid', gridTemplateColumns: '32px 1fr', gap: 2,
                alignItems: 'flex-start',
                '&:last-of-type': { borderBottom: 'none' },
              }}>
                <Box sx={{
                  width: 32, height: 32,
                  bgcolor: C.ink, color: C.gold,
                  borderRadius: '50%',
                  display: 'grid', placeItems: 'center',
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 16, flexShrink: 0,
                }}>
                  {num}
                </Box>
                <Box>
                  <Box sx={{ display: 'block', color: C.ink, fontSize: 15, fontWeight: 700, mb: 0.25 }}>
                    {title}
                  </Box>
                  <Box sx={{ fontSize: 14, color: C.inkSoft }}>{body}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardMock />
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const DashboardMock = () => (
  <Box sx={{
    bgcolor: C.paper2, border: `1px solid ${C.line}`,
    borderRadius: '12px', p: 3,
    boxShadow: '0 24px 48px rgba(26,58,42,0.08)',
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
      <Box sx={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: 18, letterSpacing: '1px', color: C.ink,
      }}>
        HR Health Insights
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        {[
          ['Overview', true],
          ['Trends', false],
          ['Compliance', false],
        ].map(([label, active]) => (
          <Box key={label} sx={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 9, letterSpacing: '1.5px',
            px: 1.25, py: 0.5, borderRadius: 0.5,
            textTransform: 'uppercase',
            bgcolor: active ? C.teal : C.paper,
            color:   active ? C.paper : C.muted,
            border: `1px solid ${active ? C.teal : C.line}`,
          }}>
            {label}
          </Box>
        ))}
      </Box>
    </Box>

    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      {[
        ['2,847', 'Employees Screened',  C.teal],
        ['94%',   'Participation Rate',  C.ink],
        ['342',   'Borderline Cases',    C.gold],
        ['11',    'Active Locations',    C.ink],
      ].map(([num, lbl, color]) => (
        <Grid item xs={6} key={lbl}>
          <Box sx={{
            bgcolor: C.paper, borderRadius: 1, p: 2,
            border: `1px solid ${C.line}`,
          }}>
            <Box sx={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 28, lineHeight: 1, color, letterSpacing: '1px',
            }}>
              {num}
            </Box>
            <Box sx={{
              fontFamily: "'Space Mono', monospace",
              fontSize: 9, letterSpacing: '1.5px',
              color: C.muted, textTransform: 'uppercase', mt: 0.5,
            }}>
              {lbl}
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>

    <Box sx={{ bgcolor: C.paper, borderRadius: 1, border: `1px solid ${C.line}`, p: 2 }}>
      <Box sx={{
        fontFamily: "'Space Mono', monospace",
        fontSize: 10, letterSpacing: '1.5px',
        color: C.muted, textTransform: 'uppercase', mb: 1.5,
      }}>
        Vitamin D · Avg level by quarter
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, height: 80 }}>
        {[35, 45, 55, 62, 70, 78, 88].map((h, i) => (
          <Box key={i} sx={{
            flex: 1, height: `${h}%`,
            bgcolor: C.teal, opacity: 0.85,
            borderRadius: '2px 2px 0 0',
          }} />
        ))}
      </Box>
    </Box>
  </Box>
);

// ─── Industries ────────────────────────────────────────────────
const Industries = () => (
  <Box sx={{ bgcolor: C.paper2, py: { xs: 8, md: 12 }, px: { xs: 2.5, md: 4 } }}>
    <Container maxWidth="lg">
      <SectionHeading
        eyebrow="Industries we serve"
        title={<>Built for <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>every workforce</Box>.</>}
        lede="We've designed programmes for shop-floor manufacturing, sleep-deprived emergency rooms, back-to-back IT campuses, and 200-store retail chains. Different industries, same standard of care."
      />
      <Grid container spacing={2}>
        {INDUSTRIES.map((i) => (
          <Grid item xs={6} sm={4} md={3} key={i.code}>
            <Box sx={{
              bgcolor: C.paper, border: `1px solid ${C.line}`,
              borderRadius: '6px',
              p: 3.5, textAlign: 'center',
              transition: 'all 0.2s',
              '&:hover': { borderColor: C.teal, transform: 'translateY(-2px)' },
            }}>
              <Box sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 24, color: C.teal,
                letterSpacing: '1px', mb: 1,
              }}>
                {i.code}
              </Box>
              <Box sx={{ fontSize: 14, fontWeight: 700, color: C.ink, mb: 0.5 }}>
                {i.name}
              </Box>
              <Box sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 10, letterSpacing: '1px',
                color: C.muted, textTransform: 'uppercase',
              }}>
                {i.meta}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

// ─── FAQ ───────────────────────────────────────────────────────
const FaqSection = () => {
  const [open, setOpen] = useState(null);
  const toggle = (idx) => setOpen((cur) => (cur === idx ? null : idx));
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, px: { xs: 2.5, md: 4 } }}>
      <SectionHeading
        eyebrow="FAQ"
        title={<>What HR teams <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>usually ask</Box>.</>}
      />
      <Box sx={{ maxWidth: 880, mx: 'auto' }}>
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <Box key={f.q} sx={{ borderBottom: `1px solid ${C.line}` }}>
              <Box component="button" type="button" onClick={() => toggle(i)}
                sx={{
                  width: '100%', bgcolor: 'transparent', border: 'none',
                  py: 2.5, textAlign: 'left',
                  fontFamily: 'inherit', fontSize: 16, fontWeight: 700,
                  color: C.ink, cursor: 'pointer',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3,
                }}>
                <span>{f.q}</span>
                <Box component="span" sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 24, color: C.teal,
                  transition: 'transform 0.2s',
                  transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                  flexShrink: 0,
                }}>
                  +
                </Box>
              </Box>
              <Box sx={{
                maxHeight: isOpen ? 400 : 0, overflow: 'hidden',
                transition: 'max-height 0.3s, padding 0.3s',
                color: C.inkSoft, fontSize: 14.5, lineHeight: 1.6,
                pb: isOpen ? 2.5 : 0,
              }}>
                {f.a}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Container>
  );
};

// Build a prefilled mailto draft from the current form state. Used as a
// fallback link in the error banner so the lead doesn't get stranded if
// the API request fails.
const buildMailto = (form) => {
  const subject = `Corporate Health Enquiry — ${form.company || 'Company'}`;
  const body = [
    `Company: ${form.company}`,
    `Industry: ${form.industry}`,
    `Contact: ${form.contact_name} (${form.designation})`,
    `Email: ${form.email}`,
    `Phone: ${form.phone}`,
    `Employees: ${form.employees}`,
    `Locations: ${form.locations}`,
    `Programmes of interest: ${form.programs.join(', ')}`,
    `Timeline: ${form.timeline}`,
    `Current health partner: ${form.incumbent}`,
    '',
    'Brief / requirements:',
    form.notes,
  ].join('\n');
  return `mailto:${CRM_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

// ─── Lead form ─────────────────────────────────────────────────
const LeadFormSection = () => {
  const initial = useMemo(() => ({
    company: '', industry: '', contact_name: '', designation: '',
    email: '', phone: '', employees: '', locations: '',
    programs: [], timeline: '', incumbent: '', notes: '',
    consent: false,
  }), []);

  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ kind: '', message: '', mailto: '' });
  const formRef = useRef(null);
  const [submit, { isLoading: submitting }] = useSubmitCorporateEnquiryMutation();

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const togglePgm = (val) =>
    setForm((f) => ({
      ...f,
      programs: f.programs.includes(val)
        ? f.programs.filter((v) => v !== val)
        : [...f.programs, val],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ kind: '', message: '', mailto: '' });

    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }
    if (!form.consent) {
      setStatus({ kind: 'error', message: 'Please tick the consent box to send your enquiry.', mailto: '' });
      return;
    }

    try {
      const resp = await submit({
        ...form,
        source_page: window.location.href,
      }).unwrap();
      setStatus({
        kind: 'success',
        message: resp?.message || "Enquiry received. Our corporate health team will reach out within 1 working day.",
        mailto: '',
      });
      setForm(initial);
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err?.data?.message || "Couldn't submit through the form.",
        mailto: buildMailto(form),
      });
    }
  };

  return (
    <Box id="enquiry" sx={{
      bgcolor: C.paper, py: { xs: 8, md: 12 }, px: { xs: 2.5, md: 4 },
      borderTop: `1px solid ${C.line}`,
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <Eyebrow>Talk to us</Eyebrow>
            <Box component="h2" sx={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: { xs: 30, sm: 40, md: 56 },
              lineHeight: 1.0, letterSpacing: '1px',
              color: C.ink, m: 0, mb: 2,
            }}>
              Request a <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>corporate quote</Box>.
            </Box>
            <Box sx={{ fontSize: 15, color: C.inkSoft }}>
              Tell us about your workforce. Our corporate health team will reach out within 1 working day
              to set up a discovery call and build a costed proposal around your specific brief.
            </Box>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 4 }}>
              {[
                'Discovery call within 1 working day of submission',
                'Costed proposal within 3 working days of brief',
                'Single account manager from quote to dashboard',
                'Pilot option available for 100+ employee engagements',
                'Multi-year and group rate discounts',
              ].map((p) => (
                <Box component="li" key={p} sx={{
                  display: 'flex', gap: 1.5, alignItems: 'flex-start',
                  py: 1.25, fontSize: 14, color: C.inkSoft,
                  '&::before': {
                    content: '"✓"', color: C.teal,
                    fontWeight: 700, flexShrink: 0,
                  },
                }}>
                  {p}
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box sx={{
              bgcolor: C.paper2, border: `1px solid ${C.line}`,
              borderRadius: '6px', p: { xs: 3, md: 5 },
            }}>
              <Box component="form" ref={formRef} onSubmit={onSubmit} noValidate>
                <FormGrid>
                  <FormField label="Company Name" required>
                    <Input name="company" type="text" required value={form.company} onChange={setField('company')} />
                  </FormField>
                  <FormField label="Industry" required>
                    <Select name="industry" required value={form.industry} onChange={setField('industry')}>
                      <option value="">Select industry</option>
                      {INDUSTRY_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                    </Select>
                  </FormField>
                </FormGrid>

                <FormGrid>
                  <FormField label="Contact Person" required>
                    <Input name="contact_name" type="text" required autoComplete="name"
                      value={form.contact_name} onChange={setField('contact_name')} />
                  </FormField>
                  <FormField label="Designation" required>
                    <Input name="designation" type="text" required
                      placeholder="e.g., HR Head, Admin Manager, CHRO"
                      value={form.designation} onChange={setField('designation')} />
                  </FormField>
                </FormGrid>

                <FormGrid>
                  <FormField label="Business Email" required>
                    <Input name="email" type="email" required autoComplete="email"
                      value={form.email} onChange={setField('email')} />
                  </FormField>
                  <FormField label="Phone" required>
                    <Input name="phone" type="tel" required pattern="[0-9+\s\-]{10,15}" autoComplete="tel"
                      value={form.phone} onChange={setField('phone')} />
                  </FormField>
                </FormGrid>

                <FormGrid>
                  <FormField label="Approx. Employees" required>
                    <Select name="employees" required value={form.employees} onChange={setField('employees')}>
                      <option value="">Select size</option>
                      {SIZE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Locations of Operation">
                    <Input name="locations" type="text" placeholder="e.g., Hyderabad, Bengaluru, Chennai"
                      value={form.locations} onChange={setField('locations')} />
                  </FormField>
                </FormGrid>

                <FormField label="Programmes of Interest">
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 1, mt: 0.5,
                  }}>
                    {PROGRAM_OPTIONS.map(([val, lbl]) => {
                      const checked = form.programs.includes(val);
                      return (
                        <Box component="label" key={val} sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          px: 1.5, py: 1.25,
                          bgcolor: checked ? 'rgba(0,180,154,0.08)' : C.paper,
                          border: `1px solid ${checked ? C.teal : C.line}`,
                          borderRadius: 0.5,
                          fontSize: 13, cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}>
                          <Box component="input" type="checkbox"
                            checked={checked} onChange={() => togglePgm(val)}
                            sx={{ accentColor: C.teal }}
                          />
                          {lbl}
                        </Box>
                      );
                    })}
                  </Box>
                </FormField>

                <Box sx={{ height: 16 }} />

                <FormGrid>
                  <FormField label="Implementation Timeline">
                    <Select name="timeline" value={form.timeline} onChange={setField('timeline')}>
                      <option value="">Select timeline</option>
                      {TIMELINE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Current Health Partner (if any)">
                    <Input name="incumbent" type="text" placeholder="Optional"
                      value={form.incumbent} onChange={setField('incumbent')} />
                  </FormField>
                </FormGrid>

                <FormField label="Brief / Specific Requirements">
                  <Textarea name="notes" value={form.notes} onChange={setField('notes')}
                    placeholder="Any context that helps us prepare for the discovery call — workforce profile, statutory obligations, prior gaps, or specific tests." />
                </FormField>

                <Box sx={{
                  display: 'flex', gap: 1.5, alignItems: 'flex-start',
                  bgcolor: C.paper, p: 2, borderRadius: 0.5,
                  my: 2.5, fontSize: 13, color: C.inkSoft,
                  border: `1px solid ${C.line}`,
                }}>
                  <Box component="input" type="checkbox" name="consent" id="corp-consent" required
                    checked={form.consent} onChange={setField('consent')}
                    sx={{ mt: '3px', accentColor: C.teal }} />
                  <Box component="label" htmlFor="corp-consent" sx={{ flex: 1, cursor: 'pointer' }}>
                    I consent to TrustLab Diagnostics processing this enquiry in line with the{' '}
                    <Box component="a" href="/privacy" sx={{ color: C.teal, textDecoration: 'underline' }}>
                      Privacy Policy
                    </Box>.{' '}
                    <Box component="span" sx={{ color: C.teal }}>*</Box>
                  </Box>
                </Box>

                <Box component="button" type="submit" disabled={submitting} sx={{
                  bgcolor: C.ink, color: C.paper,
                  border: 'none', p: '16px 32px', borderRadius: 0.5,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                  letterSpacing: 2, textTransform: 'uppercase',
                  transition: 'all 0.15s', width: '100%',
                  opacity: submitting ? 0.5 : 1,
                  '&:hover': { bgcolor: submitting ? C.ink : C.teal },
                }}>
                  {submitting ? 'Submitting…' : 'Request Corporate Quote'}
                </Box>

                {status.kind && (
                  <Box sx={{
                    mt: 2, p: '14px 18px', borderRadius: 0.5, fontSize: 14,
                    bgcolor: status.kind === 'success' ? 'rgba(0,180,154,0.1)' : 'rgba(176,66,26,0.1)',
                    color:   status.kind === 'success' ? C.tealDark : C.danger,
                    border:  `1px solid ${status.kind === 'success' ? C.teal : C.danger}`,
                  }}>
                    <Box component="strong" sx={{ display: 'block', mb: 0.5 }}>
                      {status.kind === 'success' ? 'Enquiry received.' : "Couldn't submit."}
                    </Box>
                    {status.message}
                    {status.mailto && (
                      <>
                        {' '}You can email us directly at{' '}
                        <Box component="a" href={status.mailto} sx={{ color: 'inherit', textDecoration: 'underline' }}>
                          {CRM_EMAIL}
                        </Box>{' '}
                        and we'll take it from there.
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// ─── Shared sub-components ────────────────────────────────────
const Eyebrow = ({ children, color }) => (
  <Box sx={{
    fontFamily: "'Space Mono', monospace",
    fontSize: 12, letterSpacing: '3px',
    color: color || C.gold,
    textTransform: 'uppercase', mb: 2.5,
  }}>
    {children}
  </Box>
);

const SectionHeading = ({ eyebrow, eyebrowColor, title, lede, inverse }) => (
  <Box sx={{ mb: { xs: 5, md: 7 } }}>
    {eyebrow && (
      <Box sx={{
        fontFamily: "'Space Mono', monospace",
        fontSize: 12, letterSpacing: '3px',
        color: eyebrowColor || C.teal,
        textTransform: 'uppercase', mb: 2,
      }}>
        {eyebrow}
      </Box>
    )}
    <Box component="h2" sx={{
      fontFamily: "'Bebas Neue', sans-serif",
      fontSize: { xs: 30, sm: 40, md: 56 },
      lineHeight: 1.0, letterSpacing: '1px',
      color: inverse ? C.paper : C.ink,
      m: 0, mb: lede ? 2.5 : 0,
    }}>
      {title}
    </Box>
    {lede && (
      <Box sx={{
        fontSize: 15.5, maxWidth: 720, lineHeight: 1.6,
        color: inverse ? 'rgba(250,248,243,0.7)' : C.inkSoft,
      }}>
        {lede}
      </Box>
    )}
  </Box>
);

const CtaLink = ({ href, variant, children }) => (
  <Box component="a" href={href} sx={{
    p: '14px 28px', borderRadius: 0.5,
    textDecoration: 'none', fontSize: 13, fontWeight: 700,
    letterSpacing: '1.5px', textTransform: 'uppercase',
    transition: 'all 0.15s',
    display: 'inline-flex', alignItems: 'center', gap: 1,
    ...(variant === 'primary' ? {
      bgcolor: C.teal, color: C.paper,
      '&:hover': { bgcolor: C.paper, color: C.ink },
    } : {
      bgcolor: 'transparent', color: C.paper,
      border: '1px solid rgba(250,248,243,0.3)',
      '&:hover': { borderColor: C.gold, color: C.gold },
    }),
  }}>
    {children}
  </Box>
);

const FormGrid = ({ children }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
    gap: 2, mb: 2,
  }}>
    {children}
  </Box>
);

const FormField = ({ label, required, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Box component="label" sx={{
      fontFamily: "'Space Mono', monospace",
      fontSize: 11, letterSpacing: '1.5px',
      color: C.ink, textTransform: 'uppercase',
      mb: 1, fontWeight: 700,
    }}>
      {label}{required && <Box component="span" sx={{ color: C.teal, ml: 0.5 }}>*</Box>}
    </Box>
    {children}
  </Box>
);

const inputBaseSx = {
  bgcolor: C.paper,
  border: `1px solid ${C.line}`,
  p: '12px 14px', borderRadius: 0.5,
  fontFamily: 'inherit', fontSize: 15,
  color: C.ink, outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:focus': {
    borderColor: C.teal,
    boxShadow: '0 0 0 3px rgba(0,180,154,0.12)',
  },
};

const Input    = (props) => <Box component="input"    {...props} sx={inputBaseSx} />;
const Select   = (props) => <Box component="select"   {...props} sx={inputBaseSx}>{props.children}</Box>;
const Textarea = (props) => <Box component="textarea" {...props} sx={{ ...inputBaseSx, minHeight: 100, resize: 'vertical' }} />;

// ═══════════════════════════════════════════════════════════════════════
// PACKAGE ARCHITECTURE — port of TL-DX-CHP-001 sections 01 → 07.
// Inserted between PackageTiers and HowWeWork. The three architecture
// cards (PEM / AHC / EHC) act as tabs — clicking one swaps the family
// detail panel rendered immediately below them.
// ═══════════════════════════════════════════════════════════════════════

const ARCH_PURPOSE = {
  paragraphs: [
    'This section establishes TrustLab Diagnostics’ corporate health checkup product architecture across three families: Pre-Employment Medical (PEM) packages for hiring fitness assessment, Annual Health Checkup (AHC) packages for workforce preventive screening, and Executive Health Checkup (EHC) packages for senior leadership.',
    'It defines test composition, the clinical and statutory rationale behind every inclusion, applicable regulatory frameworks, target customer segments, service delivery models, and the commercial framework for B2B sale to corporates, hospitals, manufacturing units and SME clusters across Hyderabad and TrustLab’s service geography.',
    'It is the single reference for BD teams pitching corporate HR, lab operations configuring panels in AIMS, the sample collection team executing on-site camps, the reporting team configuring MIS dashboards, and finance teams pricing tenders and rate contracts.',
  ],
  kpis: [
    { label: 'Total Packages',     value: '12', note: 'across 3 families' },
    { label: 'Add-on Modules',     value: '12', note: 'role & statutory' },
    { label: 'Delivery Models',    value: '4',  note: 'camp · branch · hub · home' },
    { label: 'Statutory Mandates', value: '12', note: 'addressed end-to-end' },
  ],
};

const ARCH_PRINCIPLES = [
  'Every package is anchored to a defensible clinical or statutory rationale. No test is included for revenue padding.',
  'Tier progression adds tests with clear incremental yield. A higher tier never duplicates a lower tier without adding meaningful clinical depth.',
  'Age-banded AHC tiers reflect the natural rise in chronic disease risk after 30, 40 and 50 — aligned with ICMR India and Indian Council of Cardiology screening guidance.',
  'Gender-specific screening (cervical, breast, prostate) is offered as a modular add-on rather than embedded — corporates opt in based on workforce composition and budget.',
  'All sample collection workflows comply with NABL ISO 15189 pre-analytical standards including chain of custody, identity verification, and biometric or photographic match where required.',
  'Reports are released through the AIMS portal with role-based access: candidate sees own report in full, HR sees fitness summary only (no diagnostic detail), Occupational Health Physician sees full clinical record.',
];

const ARCH_FAMILIES = [
  {
    id: 'pem',
    tag: 'Family 01',
    name: 'Pre-Employment Medical',
    meta: 'PEM · Conditional offer screen',
    rationale: 'Establishes fitness for duty, rules out communicable disease, creates baseline health record. Triggered prior to joining date by the contracting employer.',
    tiers: ['Essential', 'Standard', 'Comprehensive', 'Specialized'],
    chapterNum: '03 · Family I — Pre-Employment Medical',
    chapterTitle: 'PEM packages — calibrated to role exposure and statute.',
    intro: 'PEM packages are commissioned by employers as a condition precedent to issuance of the appointment letter or to confirmation of joining. The output is a fitness certificate signed by an Occupational Health Physician, accompanied by the underlying clinical report. Scope is calibrated to the role’s exposure profile, statutory mandate (Factories Act, Mines Act, FSSA, etc.) and the employer’s internal medical fitness policy.',
    packages: [
      {
        code: 'TL-PEM-ESSL-01', name: 'PEM-Essential', sub: 'Office & White-Collar', tier: 'Tier 1 of 4',
        meta: [
          { label: 'Use Case',         value: 'Standard pre-employment screen for office-based, non-hazardous roles where the dominant occupational risk is sedentary lifestyle and the legal mandate is general fitness affirmation.' },
          { label: 'Target Roles',     value: 'IT & ITES, BPO/KPO, finance, customer service, sales (non-field), administration, back-office.' },
          { label: 'Statutory Basis',  value: 'Voluntary employer policy. Not mandated by Factories Act for non-hazardous processes. Recommended under Indian Contract Act due-diligence framing.' },
          { label: 'Fitness Outcome',  value: 'Fit / Fit with reservations / Temporarily unfit / Unfit. Validity: 90 days from issue.' },
        ],
        panel: [
          { cat: 'Clinical Examination', rows: [
            { test: 'Detailed history & general physical examination by MBBS physician', rationale: 'Establishes baseline; identifies obvious red flags warranting deeper screen.' },
            { test: 'Anthropometry — Height, Weight, BMI, Waist circumference', rationale: 'Metabolic risk baseline; required for occupational health record.' },
            { test: 'Vital signs — BP, Pulse, SpO2, Temperature', rationale: 'Hypertension screening; cardiovascular baseline.' },
            { test: 'Vision — Snellen distance, Jaeger near, Ishihara colour', rationale: 'Standard pre-employment vision battery; relevant for screen-based work.' },
          ]},
          { cat: 'Haematology', rows: [
            { test: 'Complete Blood Count (CBC) with ESR', rationale: 'Anaemia, infection, haematological abnormality detection.' },
            { test: 'Blood Group & Rh typing', rationale: 'Mandatory for employment medical record and emergency reference.' },
          ]},
          { cat: 'Biochemistry', rows: [
            { test: 'Fasting Blood Sugar (FBS)', rationale: 'Diabetes screening; relevant for sedentary cohorts.' },
          ]},
          { cat: 'Serology', rows: [
            { test: 'HBsAg (Hepatitis B Surface Antigen)', rationale: 'Public-health screen; mandatory under most corporate medical policies.' },
            { test: 'HIV 1 & 2 antibody — with written informed consent (NACO 2007)', rationale: 'Screened only with explicit consent; result handled per NACO confidentiality protocol.' },
          ]},
          { cat: 'Urinalysis',  rows: [{ test: 'Urine routine & microscopy', rationale: 'Renal, glycosuric, infective screen.' }] },
          { cat: 'Imaging',     rows: [{ test: 'Chest X-Ray (PA view)', rationale: 'Tuberculosis screen — high-prevalence country requirement.' }] },
          { cat: 'Cardiac',     rows: [{ test: 'Resting ECG (12-lead) — candidates aged 40+', rationale: 'Age-appropriate baseline cardiac screen.' }] },
        ],
      },
      {
        code: 'TL-PEM-STND-02', name: 'PEM-Standard', sub: 'General Workforce', tier: 'Tier 2 of 4',
        meta: [
          { label: 'Use Case',         value: 'Pre-employment screen for manufacturing, warehousing, logistics, retail and field-sales roles with moderate physical demands and intermittent occupational exposure.' },
          { label: 'Target Roles',     value: 'Production operators, supervisors, QC technicians, warehouse staff, field sales, technicians, light vehicle drivers (non-DGMS).' },
          { label: 'Statutory Basis',  value: 'Factories Act 1948 §41C (where Schedule applies); shop floor employer due diligence under industry HSE policy.' },
          { label: 'Fitness Outcome',  value: 'Fit / Fit with reservations / Temporarily unfit / Unfit, with documented limitations relevant to physical demand level. Validity: 90 days.' },
        ],
        panel: [
          { cat: 'Foundation', rows: [{ test: 'All clinical, haematology, biochemistry, serology, urinalysis & imaging of PEM-Essential', rationale: 'Foundational screen carried forward.' }] },
          { cat: 'Audiology',  rows: [{ test: 'Pure-tone audiometry (250–8000 Hz, both ears)', rationale: 'Baseline hearing — mandatory for any role with potential noise exposure above 85 dB(A) per Factories Act Schedule.' }] },
          { cat: 'Pulmonary',  rows: [{ test: 'Pulmonary Function Test — Spirometry (FEV1, FVC, ratio)', rationale: 'Baseline respiratory function for dust, fume or aerosol exposure roles.' }] },
          { cat: 'Biochemistry — Extended', rows: [
            { test: 'Liver Function Panel — Total Bilirubin, SGPT/ALT, SGOT/AST', rationale: 'Hepatic baseline; alcohol-misuse and hepatitis context.' },
            { test: 'Renal Function Panel — Blood Urea, Serum Creatinine', rationale: 'Renal baseline; relevant for solvent and chemical exposure.' },
            { test: 'Lipid Profile — Total Chol, HDL, LDL, Triglycerides', rationale: 'Cardiovascular risk stratification.' },
          ]},
          { cat: 'Serology — Extended', rows: [{ test: 'VDRL / RPR (Syphilis screen)', rationale: 'Statutory inclusion in many industry occupational health protocols.' }] },
          { cat: 'Cardiac', rows: [{ test: 'Resting ECG — for ALL candidates regardless of age', rationale: 'Mandatory for physically demanding roles.' }] },
        ],
      },
      {
        code: 'TL-PEM-COMP-03', name: 'PEM-Comprehensive', sub: 'Healthcare & Food Handlers', tier: 'Tier 3 of 4',
        meta: [
          { label: 'Use Case',         value: 'Statutory-grade pre-employment screen for roles with patient contact, public health responsibility, or food chain exposure where carrier-state of communicable pathogens is a regulatory disqualifier.' },
          { label: 'Target Roles',     value: 'Hospital and clinic staff, lab technicians and phlebotomists (TrustLab’s own hires included), food & beverage handlers, hotel kitchen staff, school and childcare workers, pharma manufacturing personnel.' },
          { label: 'Statutory Basis',  value: 'FSSA Licensing & Registration Regulations 2011 — annual medical fitness mandatory for FBO personnel. Bio-Medical Waste Rules 2016. State Public Health Acts.' },
          { label: 'Fitness Outcome',  value: 'Fit / Fit with reservations (vaccination required prior to deployment) / Unfit. Validity: 90 days at hire; 12 months under FSSA renewal cycle for food handlers.' },
        ],
        panel: [
          { cat: 'Foundation', rows: [{ test: 'Full PEM-Standard panel', rationale: 'Foundational screen carried forward.' }] },
          { cat: 'Hepatitis Panel', rows: [
            { test: 'Anti-HBs antibody titre (quantitative)', rationale: 'Confirms vaccination immunity — required before deploying staff into clinical or kitchen environments.' },
            { test: 'Anti-HCV antibody', rationale: 'Hepatitis C screen — mandatory for clinical and lab workforce.' },
            { test: 'HAV IgM (food handlers only)', rationale: 'Acute Hepatitis A — directly relevant to food chain transmission.' },
          ]},
          { cat: 'Tuberculosis', rows: [{ test: 'IGRA (preferred) or Mantoux Tuberculin Skin Test', rationale: 'Latent TB screen for healthcare workers per WHO and Indian National TB Programme guidelines.' }] },
          { cat: 'Stool Examination', rows: [
            { test: 'Stool routine & microscopy', rationale: 'Parasitic and protozoal carrier screen.' },
            { test: 'Stool culture — Salmonella, Shigella (food handlers only)', rationale: 'Statutory under FSSA — typhoid carrier exclusion.' },
          ]},
          { cat: 'Vaccination', rows: [{ test: 'Tdap, MMR, Varicella, Hep B, Influenza — status review and titre check', rationale: 'Required before clinical deployment per ICMR healthcare worker immunization policy.' }] },
          { cat: 'Imaging', rows: [{ test: 'Chest X-Ray PA — radiologist-interpreted with explicit TB screen comment', rationale: 'Active pulmonary TB exclusion documented.' }] },
        ],
      },
      {
        code: 'TL-PEM-SPEC-04', name: 'PEM-Specialized', sub: 'Hazardous & Regulated Roles', tier: 'Tier 4 of 4',
        meta: [
          { label: 'Use Case',         value: 'Statutory pre-employment medical for roles regulated by specific occupational safety law, or for inherent-hazard roles requiring fitness certification by a Certifying Surgeon.' },
          { label: 'Target Roles',     value: 'HMV/PSV drivers, mine workers (DGMS Form O), chemical & petrochemical plant operators, height workers and scaffolders, confined space workers, radiation workers (AERB), security & armed personnel, night-shift continuous operators.' },
          { label: 'Statutory Basis',  value: 'Mines Act 1952 & DGMS Circulars; Factories Act 1948 §41C Schedules; Atomic Energy (Radiation Protection) Rules 2004; Building & Construction Workers Act 1996; Motor Transport Workers Act 1961; CMVR for HMV licence renewals.' },
          { label: 'Fitness Outcome',  value: 'Certified Fit (with annexure of any recommended workplace adjustments) / Temporarily Unfit (re-test schedule) / Unfit for stated role. Validity: per statute (typically 12 months; 6 months for some radiation workers).' },
        ],
        panel: [
          { cat: 'Foundation', rows: [{ test: 'Full PEM-Standard panel', rationale: 'Foundational screen carried forward.' }] },
          { cat: 'Vision — Detailed', rows: [
            { test: 'Visual acuity (uncorrected and best-corrected, both eyes)', rationale: 'CMVR requirement for driver licensing.' },
            { test: 'Colour vision — Ishihara + Farnsworth D-15 confirmation', rationale: 'Mandatory for drivers, electricians, signal & railway operators.' },
            { test: 'Night vision and glare sensitivity', rationale: 'CMVR night driving fitness.' },
            { test: 'Field of vision (Confrontation; perimetry where indicated)', rationale: 'Driver and crane operator requirement.' },
          ]},
          { cat: 'Cardiac & Pulmonary', rows: [
            { test: 'Resting ECG — mandatory regardless of age', rationale: 'Cardiac fitness for hazardous duty.' },
            { test: 'PFT — FEV1, FVC, FEF 25-75, ratio (with bronchodilator response if obstructive)', rationale: 'Respiratory fitness for chemical & dust exposure.' },
          ]},
          { cat: 'Substance Use', rows: [
            { test: 'Drug-of-Abuse panel — urine immunoassay (THC, opiates, cocaine, amphetamines, BZD, barbiturates) with GC-MS confirmation', rationale: 'Mandatory for safety-critical roles per employer DOT-equivalent policy. Conducted only with documented written consent.' },
            { test: 'Breath alcohol (where indicated)', rationale: 'Driver and machinery operator screening.' },
          ]},
          { cat: 'Neurological & Vestibular', rows: [
            { test: 'Vertigo & balance — Romberg, tandem walk', rationale: 'Height workers, drivers, crane & forklift operators.' },
            { test: 'Reflex and motor exam', rationale: 'Documented neurological baseline.' },
          ]},
          { cat: 'Role-Specific Add-ons', rows: [
            { test: 'Heavy-metal panel — Lead, Mercury, Arsenic, Cadmium', rationale: 'Pre-exposure baseline; medico-legal evidence in any future occupational claim.' },
            { test: 'Whole-body radiation badge baseline & CBC reference', rationale: 'AERB pre-employment requirement.' },
            { test: 'Psychometric / cognitive screen (security, armed, transport)', rationale: 'Role-specific fitness.' },
          ]},
        ],
      },
    ],
  },
  {
    id: 'ahc',
    tag: 'Family 02',
    name: 'Annual Health Checkup',
    meta: 'AHC · Workforce preventive cycle',
    rationale: 'Preventive screening; early detection of metabolic, cardiac and oncological risk. Age-banded by default, upgradable per role. Section 80D-eligible for the employee.',
    tiers: ['Bronze <30', 'Silver 30–40', 'Gold 40–50', 'Platinum 50+', "Women's Wellness"],
    chapterNum: '04 · Family II — Annual Health Checkup',
    chapterTitle: 'AHC packages — preventive yield, age-banded by default.',
    intro: 'AHC packages are designed for serving employees as part of the corporate annual benefit cycle. Tier selection is age-banded by default, with the corporate retaining the right to upgrade entire bands or individuals based on internal HR policy. Each tier is engineered to deliver clinically-meaningful early-detection yield at a price-point that justifies population-scale rollout.',
    introTail: 'All AHC packages qualify as ‘preventive health checkup’ for the employee under Income Tax Act §80D up to ₹5,000 per assessee — TrustLab supplies a Section 80D-compliant invoice on request. From the corporate’s perspective, AHC spend is a deductible business expense under §37(1).',
    packages: [
      {
        code: 'TL-AHC-BRNZ-01', name: 'AHC-Bronze', sub: 'Entry-Level Cadre', tier: 'Tier 1 · Under 30',
        meta: [
          { label: 'Use Case',     value: 'Preventive baseline screen for younger employees where the dominant risks are early metabolic dysfunction, vitamin and lifestyle deficiency, and undiagnosed thyroid dysfunction.' },
          { label: 'Target Cohort', value: 'Employees under 30. Trainees, fresh graduates, entry-level operations and back-office cadres.' },
          { label: 'Statutory',    value: 'Voluntary; Section 80D-eligible for employee.' },
          { label: 'Deliverable',  value: 'Annual Health Summary with red-amber-green status across system reviews; HRA score; physician comment and lifestyle recommendation.' },
        ],
        panel: [
          { cat: 'Clinical & Anthropometric', rows: [
            { test: 'MBBS physician examination, vitals, BMI, waist-hip ratio, body fat %', rationale: 'Baseline metabolic and lifestyle review.' },
            { test: 'Health Risk Assessment (HRA) — TrustLab digital form', rationale: 'Lifestyle, family history, mental wellness screen.' },
          ]},
          { cat: 'Haematology', rows: [{ test: 'CBC with ESR & Peripheral Smear', rationale: 'Anaemia, especially relevant in Indian female cohorts.' }] },
          { cat: 'Diabetes',    rows: [{ test: 'FBS + HbA1c', rationale: 'Younger cohort: pre-diabetes detection is the primary win.' }] },
          { cat: 'Lipid Profile', rows: [{ test: 'Total Chol, HDL, LDL, TG, VLDL, TC/HDL ratio', rationale: 'CVD risk baseline; documented dyslipidaemia in Indian under-30 cohort is meaningful.' }] },
          { cat: 'Liver & Kidney', rows: [
            { test: 'LFT — Bili, SGPT, SGOT, ALP, Total Protein, Albumin', rationale: 'Sedentary lifestyle hepatic screen.' },
            { test: 'KFT — Urea, Creatinine, Uric Acid, eGFR', rationale: 'Renal baseline.' },
          ]},
          { cat: 'Thyroid & Vitamins', rows: [
            { test: 'TSH', rationale: 'Indian cohort under-30 has high subclinical hypothyroidism prevalence.' },
            { test: 'Vitamin D3 (25-OH)', rationale: 'Documented epidemic-scale deficiency in Indian indoor-working cohorts.' },
            { test: 'Vitamin B12', rationale: 'Vegetarian cohort relevance.' },
          ]},
          { cat: 'Urinalysis', rows: [{ test: 'Urine routine & microscopy', rationale: 'Standard.' }] },
          { cat: 'Cardiac',    rows: [{ test: 'Resting 12-lead ECG', rationale: 'Cardiac baseline; arrhythmia and conduction screen.' }] },
          { cat: 'Imaging',    rows: [{ test: 'Chest X-Ray PA', rationale: 'Standard.' }] },
        ],
      },
      {
        code: 'TL-AHC-SLVR-02', name: 'AHC-Silver', sub: 'Mid-Career Cadre', tier: 'Tier 2 · 30 to 40',
        meta: [
          { label: 'Use Case',     value: 'Preventive screen for the 30–40 cohort where metabolic syndrome, early cardiac risk and stress-related morbidity become statistically significant.' },
          { label: 'Target Cohort', value: 'Employees aged 30 to 40. Team leads, individual contributors with 5–10 years of service.' },
          { label: 'Statutory',    value: 'Voluntary; Section 80D-eligible.' },
          { label: 'Deliverable',  value: 'Annual Health Summary plus stratified cardiac risk score (Framingham / WHO-ISH) plus metabolic syndrome flag.' },
        ],
        panel: [
          { cat: 'Foundation', rows: [{ test: 'Full AHC-Bronze panel', rationale: 'Foundational screen.' }] },
          { cat: 'Diabetes & Metabolic', rows: [
            { test: 'FBS + PPBS + HbA1c', rationale: 'Adds post-prandial sensitivity for early Type 2 detection.' },
            { test: 'Fasting Insulin and HOMA-IR (computed)', rationale: 'Insulin resistance — earliest detectable metabolic syndrome marker.' },
          ]},
          { cat: 'Cardiac Risk', rows: [
            { test: 'hs-CRP (high-sensitivity C-Reactive Protein)', rationale: 'Inflammatory CVD risk marker.' },
            { test: 'Lipoprotein(a) — once-in-lifetime', rationale: 'Genetic CVD risk factor; not modifiable but actionable for therapy intensification.' },
          ]},
          { cat: 'Liver Extended', rows: [{ test: 'GGT (Gamma-Glutamyl Transferase)', rationale: 'Sensitive marker for fatty liver and alcohol-related injury.' }] },
          { cat: 'Imaging Add',    rows: [{ test: 'USG Abdomen & Pelvis (KUB + HBS)', rationale: 'Fatty liver detection; gallstones; renal stones; high yield in Indian middle-class cohort.' }] },
          { cat: 'Specialist',     rows: [{ test: 'Physician consultation post-report (in-person / tele) — one session', rationale: 'Lifestyle counsel; risk discussion.' }] },
        ],
      },
      {
        code: 'TL-AHC-GOLD-03', name: 'AHC-Gold', sub: 'Senior Cadre', tier: 'Tier 3 · 40 to 50',
        meta: [
          { label: 'Use Case',     value: 'Preventive screen with cancer-screening initiation and detailed cardiac evaluation for the 40–50 cohort.' },
          { label: 'Target Cohort', value: 'Employees aged 40 to 50. Senior managers, senior individual contributors, branch heads.' },
          { label: 'Statutory',    value: 'Voluntary; Section 80D-eligible.' },
          { label: 'Deliverable',  value: 'Comprehensive report with cardiac risk profile, malignancy screen, and structured physician note.' },
        ],
        panel: [
          { cat: 'Foundation', rows: [{ test: 'Full AHC-Silver panel', rationale: 'Foundational screen.' }] },
          { cat: 'Cancer Screening', rows: [
            { test: 'Stool Occult Blood (FIT)', rationale: 'Colorectal screen — initiation at 45 per international guidelines, age 40 with family history.' },
            { test: 'PSA — Total + Free (men 45+)', rationale: 'Prostate screen — discussed with physician; not recommended in lower tiers.' },
            { test: 'AFP, CEA — context-driven (smokers, family history)', rationale: 'Hepatocellular and colorectal markers.' },
          ]},
          { cat: 'Cardiac — Detailed', rows: [
            { test: 'TMT — Treadmill Stress Test (modified Bruce protocol)', rationale: 'Functional cardiac stress assessment.' },
            { test: '2D Echocardiography', rationale: 'Structural heart screen.' },
          ]},
          { cat: 'Endocrine', rows: [
            { test: 'Complete Thyroid Profile (T3, T4, TSH)', rationale: 'Clinical thyroid status.' },
            { test: 'Cortisol (8 AM)', rationale: 'Stress and adrenal screen.' },
          ]},
          { cat: 'Bone Health', rows: [{ test: 'Serum Calcium, Phosphorus, ALP, Vitamin D3', rationale: 'Bone metabolism baseline; perimenopausal women cohort relevance.' }] },
          { cat: 'Specialist',  rows: [{ test: 'Physician + Cardiologist consultations post-report', rationale: 'Two specialist reviews bundled.' }] },
        ],
      },
      {
        code: 'TL-AHC-PLAT-04', name: 'AHC-Platinum', sub: 'Leadership Cadre', tier: 'Tier 4 · 50+',
        meta: [
          { label: 'Use Case',     value: 'Comprehensive annual screen for the 50+ cohort where cardiac, cancer and degenerative disease prevalence rises sharply.' },
          { label: 'Target Cohort', value: 'Employees aged 50+. Leadership team, division heads.' },
          { label: 'Statutory',    value: 'Voluntary; Section 80D-eligible.' },
          { label: 'Deliverable',  value: 'Detailed report covering cardiac, oncology, renal, hepatic, endocrine and bone-health systems with specialist consensus note.' },
        ],
        panel: [
          { cat: 'Foundation', rows: [{ test: 'Full AHC-Gold panel', rationale: 'Foundational screen.' }] },
          { cat: 'Advanced Cardiac', rows: [
            { test: 'CT Coronary Calcium Score (CACS) — 5-yearly', rationale: 'Atherosclerotic plaque burden — strongest single non-invasive predictor of future cardiac events.' },
            { test: 'NT-proBNP', rationale: 'Heart failure screen.' },
          ]},
          { cat: 'Cancer Screening', rows: [
            { test: 'Stool Occult Blood (FIT) — annually', rationale: 'Continuing colorectal screen.' },
            { test: 'PSA Total + Free (men) — annually', rationale: 'Annual.' },
            { test: 'Low-Dose Chest CT — for current and former smokers, biennially', rationale: 'Lung cancer screen per USPSTF; emerging India CTRI guidance.' },
          ]},
          { cat: 'Eye & Vision', rows: [{ test: 'Comprehensive ophthalmology — refraction, fundus, IOP, glaucoma screen', rationale: 'Glaucoma and diabetic retinopathy screen become critical post-50.' }] },
          { cat: 'Cognitive',    rows: [{ test: 'Mini-Mental State Exam (MMSE) or MoCA — baseline', rationale: 'Early dementia and cognitive change screen; baseline for future comparison.' }] },
          { cat: 'Specialist',   rows: [{ test: 'Physician + Cardiologist + Ophthalmologist + Nutritionist', rationale: 'Multi-disciplinary review bundled.' }] },
        ],
      },
      {
        code: 'TL-AHC-WMNS-A', name: "Women's Wellness Add-on", sub: 'Pairs with any AHC tier', tier: 'Modular Add-on',
        meta: [
          { label: 'Use Case',     value: 'Modular gender-specific screen that pairs with any AHC base tier (Bronze through Platinum). Designed for corporates with significant female workforce or as a standalone package on International Women’s Day campaigns.' },
          { label: 'Target Cohort', value: 'Female employees of all age bands. Tests gated by age (e.g. mammography only 40+).' },
          { label: 'Statutory',    value: 'Voluntary; Section 80D-eligible.' },
          { label: 'Deliverable',  value: 'Add-on report appended to base AHC report; female gynaecologist note included.' },
        ],
        panel: [
          { cat: 'Cervical & Pelvic', rows: [
            { test: 'PAP Smear (LBC — Liquid-Based Cytology)', rationale: 'Cervical cancer screen — every 3 years post-21 per ICMR / WHO.' },
            { test: 'HPV DNA co-test (women 30+) — 5-yearly', rationale: 'High-risk HPV strain screen, current standard of care.' },
            { test: 'Pelvic USG — Transabdominal; Transvaginal where indicated', rationale: 'Ovarian, uterine, fibroid, PCOS screen.' },
          ]},
          { cat: 'Breast', rows: [
            { test: 'Clinical Breast Examination by trained Female Physician', rationale: 'Annual.' },
            { test: 'Breast USG (women under 40, or as primary in dense breast tissue)', rationale: 'Sensitive in younger Indian women with dense breast pattern.' },
            { test: 'Digital Mammography (women 40+, biennially)', rationale: 'International standard breast cancer screen.' },
          ]},
          { cat: 'Hormonal & Reproductive', rows: [
            { test: 'FSH, LH, Prolactin, Estradiol — context-driven', rationale: 'Targeted only when HRA flags PCOS, perimenopausal, fertility concern.' },
            { test: 'AMH — fertility-planning context', rationale: 'Optional; only by specific consent.' },
          ]},
          { cat: 'Bone & Iron', rows: [
            { test: 'DEXA — women 50+ or earlier with risk factors', rationale: 'Osteoporosis screen — Indian post-menopausal cohort relevance.' },
            { test: 'Serum Ferritin, Iron, TIBC', rationale: 'Iron deficiency anaemia is highly prevalent in Indian women across age bands.' },
          ]},
          { cat: 'Specialist', rows: [{ test: 'Female Gynaecologist consultation post-report', rationale: 'Mandatory review of all screen results.' }] },
        ],
      },
    ],
  },
  {
    id: 'ehc',
    tag: 'Family 03',
    name: 'Executive Health Checkup',
    meta: 'EHC · Premium leadership review',
    rationale: 'Comprehensive multi-system screening with specialist consultation, premium service delivery, and detailed narrative report. Single-visit, white-glove experience.',
    tiers: ['Director', 'CXO', 'Premium'],
    gold: true,
    chapterNum: '05 · Family III — Executive Health Checkup',
    chapterTitle: 'EHC packages — premium-tier, white-glove.',
    intro: 'EHC packages are differentiated from AHC by depth of investigation, white-glove service experience, multi-specialist consensus, and the comprehensive narrative report delivered to the executive. Pricing is positioned at premium tier; the buyer is typically the CHRO or CEO’s office, not procurement.',
    introTail: 'All EHC packages are delivered at TrustLab’s flagship hub branch with a dedicated executive floor: private reception, sequential test scheduling to compress total examination time, and concierge-style coordination throughout.',
    packages: [
      {
        code: 'TL-EHC-DIRC-01', name: 'EHC-Director', sub: 'Single-Visit Executive Screen', tier: 'Tier 1 of 3 · ~4 hours', gold: true,
        meta: [
          { label: 'Use Case',     value: 'Comprehensive single-visit annual screen for senior executives, division heads and board members below CXO level.' },
          { label: 'Target Cohort', value: 'Directors, VPs, AVPs, Senior Practice Heads. Aged ~35 to 55. Sponsored by employer or self-funded under executive benefit policy.' },
          { label: 'Statutory',    value: 'Voluntary; Section 80D-eligible.' },
          { label: 'Deliverable',  value: 'Detailed clinical report with system-wise narrative, risk stratification, action plan, and 30-minute Internal Medicine consultation. ~4 hours on premises.' },
        ],
        panel: [
          { cat: 'Foundation', rows: [{ test: 'Full AHC-Gold clinical and laboratory panel', rationale: 'Foundational screen.' }] },
          { cat: 'Cardiac — Comprehensive', rows: [
            { test: 'TMT — full Bruce protocol', rationale: 'Functional cardiac assessment.' },
            { test: '2D Echocardiography with Colour Doppler', rationale: 'Structural and functional heart assessment.' },
            { test: 'NT-proBNP', rationale: 'Heart failure marker.' },
          ]},
          { cat: 'Imaging', rows: [
            { test: 'USG Abdomen & Pelvis', rationale: 'Hepatic, renal, splenic, gallbladder, pelvic survey.' },
            { test: 'X-Ray Chest PA & Lateral', rationale: 'Standard.' },
          ]},
          { cat: 'Cancer Screening', rows: [
            { test: 'Tumour markers — CEA, AFP, PSA (men), CA-125 (women), CA 19-9', rationale: 'Targeted; explicit physician counselling on interpretation; never reported in isolation.' },
            { test: 'Stool occult blood (FIT)', rationale: 'Colorectal screen.' },
          ]},
          { cat: 'Endocrine & Metabolic', rows: [
            { test: 'Complete Thyroid (T3, T4, FT3, FT4, TSH, Anti-TPO)', rationale: 'Detailed thyroid status.' },
            { test: 'Cortisol (AM and PM if indicated)', rationale: 'Adrenal screen.' },
            { test: 'HbA1c, Fasting Insulin, HOMA-IR', rationale: 'Detailed glycaemic and resistance profile.' },
          ]},
          { cat: 'Specialist Consultation', rows: [
            { test: 'Internal Medicine specialist — 30 min (final consensus)', rationale: 'Integrative review.' },
            { test: 'Cardiologist — 15 min (TMT and Echo review)', rationale: 'Targeted review.' },
            { test: 'Nutritionist — 30 min', rationale: 'Personalised plan.' },
          ]},
          { cat: 'Hospitality', rows: [{ test: 'Executive lounge access; healthy breakfast post-fasting; chauffeur drop arrangement', rationale: 'Brand experience.' }] },
        ],
      },
      {
        code: 'TL-EHC-CXOX-02', name: 'EHC-CXO', sub: 'Half-Day Premium Screen', tier: 'Tier 2 of 3 · ~5–6 hours', gold: true,
        meta: [
          { label: 'Use Case',     value: 'Premium half-day screen with detailed cardiac, metabolic and oncology assessment, multi-specialist consensus, and a structured narrative report bound and delivered.' },
          { label: 'Target Cohort', value: 'C-suite — CEO, COO, CFO, CTO, CHRO, CRO. Founders, MDs, board members. Aged 40+.' },
          { label: 'Statutory',    value: 'Voluntary; Section 80D-eligible.' },
          { label: 'Deliverable',  value: 'Comprehensive Executive Health Report (printed + digital) with structured 5-year health trajectory section if executive has prior TrustLab history.' },
        ],
        panel: [
          { cat: 'Foundation', rows: [{ test: 'Full EHC-Director panel', rationale: 'Foundational screen.' }] },
          { cat: 'Advanced Cardiac', rows: [
            { test: 'CT Coronary Calcium Score (CACS)', rationale: 'Atherosclerotic plaque burden quantification.' },
            { test: 'Carotid Intima-Media Thickness (CIMT) Doppler', rationale: 'Subclinical atherosclerosis — high-yield in middle-aged Indian executives.' },
            { test: 'Apolipoprotein A1, Apolipoprotein B, Apo B/A1 ratio', rationale: 'Refined CVD risk stratification beyond LDL.' },
            { test: 'Lipoprotein(a)', rationale: 'Genetic cardiovascular risk.' },
            { test: 'Homocysteine', rationale: 'Independent CVD risk factor.' },
          ]},
          { cat: 'Cancer Screening', rows: [
            { test: 'Full tumour markers — CEA, AFP, PSA (T+F), CA-125, CA 19-9, CA 15-3, β-HCG', rationale: 'With written physician interpretation.' },
            { test: 'LDCT lung screen — for smokers and former smokers', rationale: 'Lung cancer screening.' },
            { test: 'Upper GI Endoscopy + Colonoscopy — partner unit, optional add-on', rationale: 'Direct mucosal screen for high-risk individuals.' },
          ]},
          { cat: 'Metabolic & Hormonal', rows: [
            { test: '75g OGTT (where indicated)', rationale: 'Pre-diabetes and impaired glucose tolerance.' },
            { test: 'Sex hormones — Total Testosterone (men), DHEA-S, SHBG', rationale: 'Andropause / age-related hormonal screen.' },
            { test: 'Vitamin B12, Folate, Vitamin D3, Magnesium, Zinc', rationale: 'Comprehensive micronutrient panel.' },
            { test: 'hs-CRP, Fibrinogen, Ferritin', rationale: 'Inflammation and oxidative stress markers.' },
          ]},
          { cat: 'Mental Wellness', rows: [
            { test: 'Burnout, anxiety & depression screen — Maslach, PHQ-9, GAD-7', rationale: 'Stress and burnout assessment for high-pressure roles.' },
            { test: 'Sleep questionnaire (Epworth + Pittsburgh) and home sleep study referral', rationale: 'Sleep apnoea screening.' },
          ]},
          { cat: 'Specialist Consultation', rows: [
            { test: 'Internal Medicine — 45 min (lead consult)', rationale: 'Integrative review.' },
            { test: 'Cardiologist — 30 min', rationale: 'Detailed cardiac counsel.' },
            { test: 'Endocrinologist — 30 min', rationale: 'Metabolic and hormonal counsel.' },
            { test: 'Nutritionist — 45 min (with follow-up)', rationale: 'Detailed personalised plan.' },
            { test: 'Mental wellness coach — 30 min (optional)', rationale: 'Stress and burnout counsel.' },
          ]},
          { cat: 'Hospitality', rows: [{ test: 'Private examination suite, executive concierge, meal service, printed bound report, 90-day teleconsult access', rationale: 'Premium experience.' }] },
        ],
      },
      {
        code: 'TL-EHC-PRMM-03', name: 'EHC-Premium', sub: 'Full-Day Multi-Specialist Screen', tier: 'Tier 3 of 3 · Full day', gold: true,
        meta: [
          { label: 'Use Case',     value: 'Most comprehensive single-visit executive screen with full-body imaging, multi-specialist convened review, and a longitudinal Health Trajectory document with 5-year forward projections.' },
          { label: 'Target Cohort', value: 'CEOs, founders, board chairs, sovereign and family-office principals, UHNI, sports professionals at peak. Aged 45+. Family-office or corporate-sponsored.' },
          { label: 'Statutory',    value: 'Voluntary; Section 80D-eligible (within statutory cap — overage is taxable perquisite).' },
          { label: 'Deliverable',  value: 'Full-body health audit. Same-day specialist consensus meeting. Comprehensive bound report with 5-year projection. 12-month concierge medical access.' },
        ],
        panel: [
          { cat: 'Foundation', rows: [{ test: 'Full EHC-CXO panel', rationale: 'Foundational screen.' }] },
          { cat: 'Advanced Imaging', rows: [
            { test: 'Whole-body MRI screen (1.5T or 3T) — non-contrast: brain, neck, chest, abdomen, pelvis, spine', rationale: 'Most comprehensive single non-invasive imaging modality; sub-centimetre resolution.' },
            { test: 'MR Angiography — circle of Willis', rationale: 'Cerebrovascular aneurysm and stenosis screen.' },
            { test: 'DEXA — bone density and body composition', rationale: 'Osteoporosis and lean-mass profile.' },
          ]},
          { cat: 'Comprehensive Cardiac', rows: [
            { test: 'CT Coronary Angiography (CCTA) — when CACS warrants', rationale: 'Direct visualisation of coronary anatomy.' },
            { test: 'Holter 24-hour ECG monitoring', rationale: 'Arrhythmia detection.' },
            { test: 'Ambulatory BP monitoring 24-hour', rationale: 'True hypertensive load.' },
          ]},
          { cat: 'Genomic / Predictive (optional)', rows: [
            { test: 'Pharmacogenomic panel — CYP2D6, CYP2C19, VKORC1', rationale: 'Drug-metabolism profile.' },
            { test: 'Cardiogenetic panel — FH, HCM, channelopathies', rationale: 'Familial cardiac risk.' },
            { test: 'Hereditary cancer panel — BRCA1/2, Lynch (with genetic counsellor pre-test)', rationale: 'Targeted only for relevant family history.' },
          ]},
          { cat: 'Pulmonology', rows: [
            { test: 'Detailed PFT with diffusion (DLCO)', rationale: 'Detailed respiratory profile.' },
            { test: 'LDCT lung screen', rationale: 'Lung cancer screening.' },
          ]},
          { cat: 'Gastroenterology', rows: [
            { test: 'Upper GI Endoscopy + Colonoscopy under sedation (age-appropriate)', rationale: 'Direct mucosal screen at gold standard.' },
            { test: 'FibroScan — liver elastography', rationale: 'Non-invasive liver fibrosis quantification.' },
          ]},
          { cat: 'Lifestyle & Performance', rows: [
            { test: 'VO2 Max — cardio-pulmonary exercise testing (CPET)', rationale: 'Aerobic capacity benchmarking.' },
            { test: 'Body composition (DEXA-derived) and resting metabolic rate', rationale: 'Performance baseline.' },
            { test: 'In-lab polysomnography — partner facility', rationale: 'Sleep architecture and apnoea quantification.' },
          ]},
          { cat: 'Multi-Specialist Consensus', rows: [{ test: 'Same-day round-table — Internal Medicine lead, Cardiologist, Endocrinologist, Oncologist, Gastroenterologist, Nutritionist, Mental Wellness', rationale: 'Single-source-of-truth executive review; not available in lower tiers.' }] },
          { cat: 'Deliverables', rows: [
            { test: 'Bound, designed Executive Health Report with 5-year projection chapter, action plan, quarterly milestones', rationale: 'Premium document.' },
            { test: '12-month concierge access — direct line to assigned Internal Medicine physician', rationale: 'Year-long relationship, not transactional.' },
            { test: 'Family discount — spouse and adult dependents at 25% off equivalent tier', rationale: 'Soft-sell to expand wallet share.' },
          ]},
        ],
      },
    ],
  },
];

const ARCH_ADDONS = [
  { code: 'TL-ADD-NOIS-01', name: 'Noise-Exposed Worker',          desc: 'Pure-tone audiometry (250–8000 Hz) every 6 months under Factories Act Schedule for noise zones above 85 dB(A); threshold-shift documentation; ear-protection fit-check.' },
  { code: 'TL-ADD-CHEM-02', name: 'Chemical / Solvent Exposure',   desc: 'Heavy metal panel (Pb, Hg, Cd, As), urinary phenol or hippuric acid (solvent-specific), LFT, KFT, CBC. Bi-annual under Factories Act Schedule.' },
  { code: 'TL-ADD-DUST-03', name: 'Dust / Particulate Exposure',   desc: 'PFT with bronchodilator response, Chest X-Ray with ILO classification (radiologist trained for pneumoconiosis grading), serial monitoring for silica, asbestos, cotton-dust workers.' },
  { code: 'TL-ADD-RADN-04', name: 'Radiation Worker (AERB)',       desc: 'CBC bi-annual, urinalysis, eye examination (lens opacity), thyroid annual. Personal dosimetry verification. AERB Form-1A medical fitness annually.' },
  { code: 'TL-ADD-NGHT-05', name: 'Night-Shift Worker',            desc: 'Sleep questionnaire (Epworth, Pittsburgh), HbA1c, lipid profile, BP profile. Recommended bi-annual under emerging shift-work occupational health policy.' },
  { code: 'TL-ADD-DRVR-06', name: 'Driver Fitness (CMVR)',         desc: 'Detailed vision (acuity, colour, field, night), audiometry, ECG, BP profile, drug screen. Required at HMV licence renewal and corporate fleet onboarding.' },
  { code: 'TL-ADD-FOOD-07', name: 'Food Handler Renewal (FSSA)',   desc: 'Stool culture (Salmonella, Shigella), HAV IgM, Hepatitis B, skin examination, throat swab. Annual under FSSA Schedule 4 for FBOs.' },
  { code: 'TL-ADD-VAXN-08', name: 'Vaccination Verification',      desc: 'Anti-HBs titre, MMR titre, Varicella titre, Tdap booster status. For healthcare and laboratory hires.' },
  { code: 'TL-ADD-CNCR-09', name: 'Cancer Screening Booster',      desc: 'Add to AHC-Gold or Platinum: tumour marker panel, LDCT lung (smokers), FIT, age-appropriate cervical, breast, prostate.' },
  { code: 'TL-ADD-CARD-10', name: 'Cardiac Booster',               desc: 'Add CACS, CIMT Doppler, Apo A/Apo B, Lp(a), Homocysteine, NT-proBNP to any AHC tier. Indicated in family history of premature CAD.' },
  { code: 'TL-ADD-MENT-11', name: 'Mental Wellness',               desc: 'PHQ-9 depression, GAD-7 anxiety, Maslach burnout, Pittsburgh sleep, Epworth sleepiness — administered digitally with mental wellness coach review.' },
  { code: 'TL-ADD-COVD-12', name: 'Post-COVID / Long-COVID',       desc: 'D-Dimer, CRP, Ferritin, NT-proBNP, PFT, 6-minute walk test, psychometric review for cognitive fog. For employees recovering from a significant COVID episode.' },
];

const ARCH_STATUTES = [
  { act: 'Factories Act 1948 — §41C & Schedule',                          effect: 'Pre-employment medical for hazardous processes; periodic medical examinations; record-keeping for inspector review. Discharged by PEM-Standard, PEM-Specialized and applicable AHC packages.' },
  { act: 'Mines Act 1952 / DGMS Circulars',                               effect: 'Form O Pre-Employment and Periodic Medical for mine workers. PEM-Specialized supports DGMS Form O fitness reporting.' },
  { act: 'FSSA Licensing & Registration Regulations 2011 — Schedule 4',   effect: 'Annual medical fitness mandatory for personnel of Food Business Operators. PEM-Comprehensive and TL-ADD-FOOD-07 designed for this.' },
  { act: 'Bio-Medical Waste Management Rules 2016',                       effect: 'Pre-deployment vaccination verification and periodic surveillance for healthcare workers handling BMW. PEM-Comprehensive and TL-ADD-VAXN-08 address this.' },
  { act: 'Atomic Energy (Radiation Protection) Rules 2004 / AERB',        effect: 'Annual Form-1A medical fitness for radiation workers. TL-ADD-RADN-04 designed for this.' },
  { act: 'Central Motor Vehicles Rules — Form 1A',                        effect: 'Driver fitness at HMV/PSV licence application and renewal. TL-ADD-DRVR-06 designed for this.' },
  { act: 'Building & Other Construction Workers Act 1996',                effect: 'Health surveillance for construction workforce. PEM-Standard and PEM-Specialized cover.' },
  { act: 'NABL ISO 15189:2022',                                           effect: 'Medical laboratory accreditation standard governing all sample collection, testing and reporting under TrustLab packages.' },
  { act: 'DPDP Act 2023',                                                 effect: 'Candidate health data is sensitive personal data. Consent, purpose limitation, retention and deletion handled through TrustLab’s DPDP framework. HR receives only fitness summaries; full report only to candidate.' },
  { act: 'NACO HIV Testing Policy 2007',                                  effect: 'Pre-test counselling, written informed consent, post-test counselling, confidentiality of result. Followed for all HIV testing across all packages.' },
  { act: 'Income Tax Act 1961 — §80D',                                    effect: 'Preventive Health Checkup deduction up to ₹5,000 per assessee. TrustLab issues Section 80D-compliant invoice on request.' },
  { act: 'Income Tax Act 1961 — §37(1)',                                  effect: 'Corporate spend on employee health is deductible business expense. TrustLab issues GSTIN-bearing tax invoice.' },
];

// ─── Chapter title (numbered eyebrow + serif-feel display title) ─────────
const ChapterHeader = ({ number, title, lede, ledeTail }) => (
  <Box sx={{ mb: { xs: 4, md: 5 } }}>
    <Box sx={{
      fontFamily: "'Space Mono', monospace",
      fontSize: 11, letterSpacing: '3px',
      color: C.teal, textTransform: 'uppercase', mb: 1.25,
    }}>
      {number}
    </Box>
    <Box component="h2" sx={{
      fontFamily: "'Bebas Neue', sans-serif",
      fontSize: { xs: 30, sm: 40, md: 52 },
      lineHeight: 1.05, letterSpacing: '1px',
      color: C.ink,
      m: 0, mb: lede ? 2 : 0,
      pb: 2, borderBottom: `1px solid ${C.line}`,
      maxWidth: 900,
    }}>
      {title}
    </Box>
    {lede && (
      <Box sx={{ fontSize: 15, lineHeight: 1.7, color: C.inkSoft, maxWidth: 780, mt: 1.5 }}>
        {lede}
      </Box>
    )}
    {ledeTail && (
      <Box sx={{ fontSize: 15, lineHeight: 1.7, color: C.inkSoft, maxWidth: 780, mt: 1.25 }}>
        {ledeTail}
      </Box>
    )}
  </Box>
);

const SubHeader = ({ children }) => (
  <Box component="h3" sx={{
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: { xs: 22, md: 28 }, letterSpacing: '1px',
    color: C.ink, m: 0, mt: { xs: 4, md: 5 }, mb: 2,
    pl: 1.5, position: 'relative',
    '&::before': {
      content: '""', position: 'absolute', left: 0, top: 6,
      width: 4, height: '1.5rem', bgcolor: C.teal,
    },
  }}>
    {children}
  </Box>
);

// ─── Package card (mini version of the HTML's .pkg block) ─────────────────
const PackageCardArch = ({ pkg }) => (
  <Box sx={{
    bgcolor: C.paper,
    border: `1px solid ${C.line}`,
    borderRadius: '4px',
    mb: { xs: 3, md: 4 },
    overflow: 'hidden',
  }}>
    {/* Dark header */}
    <Box sx={{
      bgcolor: C.ink,
      color: C.paper,
      p: { xs: 2.5, md: 3 },
      position: 'relative',
      borderBottom: pkg.gold ? `2px solid ${C.gold}` : 'none',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Box sx={{
          fontFamily: "'Space Mono', monospace", fontSize: 11,
          letterSpacing: '2.5px', textTransform: 'uppercase',
          color: pkg.gold ? C.gold : C.teal,
        }}>
          {pkg.code}
        </Box>
        <Box sx={{
          fontFamily: "'Space Mono', monospace", fontSize: 10,
          letterSpacing: '2px', textTransform: 'uppercase',
          color: pkg.gold ? C.gold : C.teal,
          border: `1px solid ${pkg.gold ? 'rgba(240,180,41,0.5)' : 'rgba(0,180,154,0.4)'}`,
          px: 1.25, py: 0.4, borderRadius: 100,
        }}>
          {pkg.tier}
        </Box>
      </Box>
      <Box sx={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: { xs: 24, md: 30 }, letterSpacing: '0.5px',
        color: C.paper, lineHeight: 1.15,
      }}>
        {pkg.name}
        {pkg.sub && (
          <Box component="span" sx={{
            fontSize: { xs: 14, md: 16 }, opacity: 0.6, ml: 1, fontWeight: 400,
          }}>
            — {pkg.sub}
          </Box>
        )}
      </Box>
    </Box>

    {/* Meta grid */}
    <Box sx={{
      display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      bgcolor: C.line, gap: '1px',
      borderBottom: `1px solid ${C.line}`,
    }}>
      {pkg.meta.map((m) => (
        <Box key={m.label} sx={{ bgcolor: C.paper2, p: { xs: 1.5, md: 2 } }}>
          <Box sx={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 10, letterSpacing: '2.5px',
            color: C.muted, textTransform: 'uppercase', mb: 0.5,
          }}>
            {m.label}
          </Box>
          <Box sx={{ fontSize: 13.5, lineHeight: 1.5, color: C.ink }}>
            {m.value}
          </Box>
        </Box>
      ))}
    </Box>

    {/* Panel table */}
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, overflowX: 'auto' }}>
      <Box component="table" sx={{
        width: '100%', borderCollapse: 'collapse', fontSize: 13.5,
      }}>
        <Box component="thead">
          <Box component="tr">
            {['Category', 'Test / Examination', 'Clinical Rationale'].map((h) => (
              <Box component="th" key={h} sx={{
                bgcolor: C.ink, color: C.paper,
                fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
                fontSize: 10.5, letterSpacing: '1.5px',
                textTransform: 'uppercase', textAlign: 'left',
                p: '10px 12px',
              }}>
                {h}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {pkg.panel.map((group) =>
            group.rows.map((row, i) => (
              <Box component="tr" key={`${group.cat}-${i}`} sx={{ '&:hover td': { bgcolor: 'rgba(0,180,154,0.03)' } }}>
                {i === 0 && (
                  <Box component="td" rowSpan={group.rows.length} sx={{
                    p: '10px 12px',
                    borderBottom: `1px solid ${C.line}`,
                    borderRight: `2px solid ${C.teal}`,
                    bgcolor: C.paper2,
                    fontWeight: 600, fontSize: 12,
                    letterSpacing: '0.5px', textTransform: 'uppercase',
                    color: C.ink, verticalAlign: 'top', width: '22%',
                  }}>
                    {group.cat}
                  </Box>
                )}
                <Box component="td" sx={{
                  p: '10px 12px', borderBottom: `1px solid ${C.line}`,
                  fontWeight: 500, color: C.ink, verticalAlign: 'top', width: '35%',
                }}>
                  {row.test}
                </Box>
                <Box component="td" sx={{
                  p: '10px 12px', borderBottom: `1px solid ${C.line}`,
                  color: C.muted, fontSize: 12.5, verticalAlign: 'top',
                }}>
                  {row.rationale}
                </Box>
              </Box>
            )),
          )}
        </Box>
      </Box>
    </Box>
  </Box>
);

const PackageArchitecture = () => {
  const [active, setActive] = useState(0);
  const fam = ARCH_FAMILIES[active];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 }, px: { xs: 2.5, md: 4 } }}>
      {/* 01 — Purpose & Scope (now first) */}
      <Box id="arch-purpose">
        <ChapterHeader
          number="01 · Purpose & Scope"
          title="A single source of truth for TrustLab’s corporate health offer."
        />
        {ARCH_PURPOSE.paragraphs.map((p, i) => (
          <Box key={i} sx={{ fontSize: 15, lineHeight: 1.7, color: C.inkSoft, maxWidth: 780, mb: 1.5 }}>{p}</Box>
        ))}
        <Grid container spacing={1.5} sx={{ mt: 3 }}>
          {ARCH_PURPOSE.kpis.map((k) => (
            <Grid item xs={6} sm={3} key={k.label}>
              <Box sx={{
                bgcolor: C.paper2, border: `1px solid ${C.line}`,
                borderLeft: `3px solid ${C.teal}`, p: 2,
              }}>
                <Box sx={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: 10, letterSpacing: '1.8px',
                  color: C.muted, textTransform: 'uppercase', mb: 0.5,
                }}>
                  {k.label}
                </Box>
                <Box sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 30, color: C.ink, lineHeight: 1.1, mb: 0.25,
                }}>
                  {k.value}
                </Box>
                <Box sx={{ fontSize: 12, color: C.muted, lineHeight: 1.4 }}>
                  {k.note}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Architecture (clickable family cards) — second; sits beneath
          the framework explanation so the user reads the "why" first. */}
      <Box id="arch-architecture" sx={{ mt: { xs: 8, md: 10 } }}>
        <ChapterHeader
          number="Sample tiers"
          title="Indicative tiers."
          lede="These are starting points, not menus. Your final panel is shaped by your workforce demographics, industry exposure profile, and budget envelope."
        />

        <Grid container spacing={2}>
          {ARCH_FAMILIES.map((f, i) => {
            const isActive = i === active;
            const isGold = f.gold;
            return (
              <Grid item xs={12} md={4} key={f.id}>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setActive(i)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActive(i); }}
                  sx={{
                    bgcolor: C.paper, p: { xs: 2.5, md: 3 },
                    border: `1px solid ${isActive ? (isGold ? C.gold : C.teal) : C.line}`,
                    borderRadius: '4px',
                    cursor: 'pointer', height: '100%',
                    transition: 'all 0.25s',
                    boxShadow: isActive ? `0 12px 32px -16px rgba(26,58,42,0.22)` : 'none',
                    transform: isActive ? 'translateY(-2px)' : 'none',
                    '&:hover': {
                      borderColor: isGold ? C.gold : C.teal,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 32px -16px rgba(26,58,42,0.18)`,
                    },
                  }}
                >
                  <Box sx={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 10, letterSpacing: '2.5px',
                    color: isGold ? C.gold : C.teal, textTransform: 'uppercase', mb: 1.5,
                  }}>
                    {f.tag}
                  </Box>
                  <Box sx={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 26, color: C.ink, lineHeight: 1.1, letterSpacing: '0.5px', mb: 1.5,
                  }}>
                    {f.name}
                  </Box>
                  <Box sx={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 11, color: C.muted, letterSpacing: '0.5px', mb: 1.5,
                    pb: 1.5, borderBottom: `1px solid ${C.line}`,
                  }}>
                    {f.meta}
                  </Box>
                  <Box sx={{ fontSize: 13.5, lineHeight: 1.55, color: C.inkSoft, mb: 1.5 }}>
                    {f.rationale}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {f.tiers.map((t) => (
                      <Box key={t} sx={{
                        fontFamily: "'Space Mono', monospace", fontSize: 11,
                        bgcolor: isGold ? 'rgba(240,180,41,0.15)' : C.paper2,
                        color: isGold ? C.gold : C.ink,
                        px: 1, py: 0.4, borderRadius: '2px',
                        letterSpacing: '0.3px', fontWeight: 500,
                      }}>
                        {t}
                      </Box>
                    ))}
                  </Box>
                  {isActive && (
                    <Box sx={{
                      mt: 2, fontFamily: "'Space Mono', monospace", fontSize: 10,
                      letterSpacing: '2px', color: isGold ? C.gold : C.teal, textTransform: 'uppercase',
                    }}>
                      ▾ Showing details below
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <SubHeader>Design Principles</SubHeader>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {ARCH_PRINCIPLES.map((p) => (
            <Box component="li" key={p} sx={{
              position: 'relative', pl: 3, mb: 1.25,
              fontSize: 14.5, lineHeight: 1.6, color: C.inkSoft,
              '&::before': {
                content: '""', position: 'absolute', left: 0, top: 12,
                width: 12, height: 1, bgcolor: C.teal,
              },
            }}>
              {p}
            </Box>
          ))}
        </Box>
      </Box>

      {/* 03 / 04 / 05 — Family detail (swaps per active card) */}
      <Box id="arch-family-detail" sx={{ mt: { xs: 8, md: 10 } }}>
        <ChapterHeader
          number={fam.chapterNum}
          title={fam.chapterTitle}
          lede={fam.intro}
          ledeTail={fam.introTail}
        />
        {fam.packages.map((pkg) => (
          <PackageCardArch key={pkg.code} pkg={pkg} />
        ))}
      </Box>

      {/* 06 — Add-ons */}
      <Box id="arch-addons" sx={{ mt: { xs: 8, md: 10 } }}>
        <ChapterHeader
          number="06 · Modular Add-ons"
          title="Role-based and statutory add-on modules."
          lede="These modules attach to any base PEM, AHC or EHC package as priced line items. They allow corporates with specific role exposures or industry mandates to compose a fitness regime exactly matched to their HSE policy without forcing a full tier upgrade."
        />
        <Grid container spacing={1.5}>
          {ARCH_ADDONS.map((a) => (
            <Grid item xs={12} md={6} key={a.code}>
              <Box sx={{
                bgcolor: C.paper, border: `1px solid ${C.line}`,
                p: 2.25,
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: C.teal },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                  <Box sx={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 20, color: C.ink, lineHeight: 1.1, letterSpacing: '0.5px',
                  }}>
                    {a.name}
                  </Box>
                  <Box sx={{
                    fontFamily: "'Space Mono', monospace", fontSize: 11,
                    color: C.teal, fontWeight: 700, letterSpacing: '0.5px',
                  }}>
                    {a.code}
                  </Box>
                </Box>
                <Box sx={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                  {a.desc}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* 07 — Statutory */}
      <Box id="arch-statutory" sx={{ mt: { xs: 8, md: 10 } }}>
        <ChapterHeader
          number="07 · Statutory & Compliance"
          title="A defensible compliance position."
          lede="All packages are designed to satisfy or exceed the relevant Indian regulatory standards. The framework below documents the statutory anchors corporates can cite when including TrustLab on their approved-vendor panel and during regulatory audits."
        />
        <SubHeader>Statutory Mandates Addressed</SubHeader>
        <Box sx={{ borderTop: `1px solid ${C.line}` }}>
          {ARCH_STATUTES.map((s) => (
            <Box
              key={s.act}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
                gap: { xs: 0.5, md: 3 },
                py: 2, alignItems: 'baseline',
                borderBottom: `1px solid ${C.line}`,
              }}
            >
              <Box sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 18, color: C.ink, lineHeight: 1.3, letterSpacing: '0.3px',
              }}>
                {s.act}
              </Box>
              <Box sx={{ fontSize: 14, color: C.inkSoft, lineHeight: 1.6 }}>
                {s.effect}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default Corporate;
